// filepath: apps/web/src/features/tasks/TaskInbox.tsx
//
// Task inbox — FR-UI-TASK-1 AC-1, AC-2, AC-3, AC-6.
//
// Renders two tabs:
//   - "My Tasks" — tasks assigned to the current user
//   - "Unclaimed (my roles)" — unclaimed tasks visible to the user's roles
//
// Handles navigation between the inbox list and the task completion form.

import React, { useState } from "react";
import { TaskRow } from "./TaskRow";
import { useTasks } from "./useTasks";
import type { HumanTask } from "./types";
import { TaskForm } from "@/renderers/task/TaskForm";
import { useTranslation } from "@/i18n/useTranslation";
import { useIsMobile } from "@/runtime/useIsMobile";

export interface TaskInboxProps {
  /** Reserved for future use (e.g. back-navigation in a full app shell). */
  onNavigateAway?: () => void;
}

type Tab = "mine" | "unclaimed";

/**
 * TaskInbox — the two-tab task inbox.
 *
 * AC-1 (My Tasks tab): shows tasks where assigned_user = current_user.
 * AC-2 (Unclaimed tab): shows unclaimed tasks visible to the user's JWT roles.
 * AC-6: overdue badge rendered per TaskRow → DueDateBadge.
 *
 * When a claimed task's "Complete" button is clicked, transitions to
 * the TaskForm view. When the task is completed or cancelled, returns
 * to the inbox list.
 */
export function TaskInbox({
  onNavigateAway: _onNavigateAway,
}: TaskInboxProps): React.ReactElement {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<Tab>("mine");
  const [claimedTask, setClaimedTask] = useState<HumanTask | null>(null);

  const {
    tasks: mineTasks,
    isLoading: isMineLoading,
    isError: isMineError,
  } = useTasks({ view: "mine" });

  const {
    tasks: unclaimedTasks,
    isLoading: isUnclaimedLoading,
    isError: isUnclaimedError,
  } = useTasks({ view: "unclaimed" });

  // When a claimed task's "Complete" button is clicked, show TaskForm.
  if (claimedTask !== null) {
    return (
      <TaskForm
        task={claimedTask}
        onCompleted={() => {
          setClaimedTask(null);
        }}
        onCancel={() => {
          setClaimedTask(null);
        }}
      />
    );
  }

  const isLoading = activeTab === "mine" ? isMineLoading : isUnclaimedLoading;
  const isError = activeTab === "mine" ? isMineError : isUnclaimedError;
  const tasks = activeTab === "mine" ? mineTasks : unclaimedTasks;

  return (
    <div data-testid="task-inbox">
      {/* Tab bar */}
      <div
        style={{
          display: "flex",
          borderBottom: "2px solid #e5e7eb",
          marginBottom: "0",
        }}
      >
        <TabButton
          active={activeTab === "mine"}
          onClick={() => setActiveTab("mine")}
          label="My Tasks"
          count={mineTasks?.length}
          isMobile={isMobile}
        />
        <TabButton
          active={activeTab === "unclaimed"}
          onClick={() => setActiveTab("unclaimed")}
          label="Unclaimed (my roles)"
          count={unclaimedTasks?.length}
          isMobile={isMobile}
        />
      </div>

      {/* Loading / error states */}
      {isLoading && (
        <div
          data-testid="task-inbox-loading"
          style={{ padding: "32px", textAlign: "center", color: "#6b7280" }}
        >
          {t("task.inbox.loading", "Loading tasks...")}
        </div>
      )}

      {isError && (
        <div
          data-testid="task-inbox-error"
          style={{ padding: "32px", textAlign: "center", color: "#dc2626" }}
        >
          Failed to load tasks. Please try again.
        </div>
      )}

      {!isLoading && !isError && tasks && tasks.length === 0 && (
        <div
          data-testid="task-inbox-empty"
          style={{ padding: "32px", textAlign: "center", color: "#9ca3af" }}
        >
          {activeTab === "mine"
            ? "You have no assigned tasks."
            : "No unclaimed tasks are available for your roles."}
        </div>
      )}

      {!isLoading && !isError && tasks && tasks.length > 0 && (
        <div
          data-testid="task-list"
          style={
            isMobile
              ? { display: "flex", flexDirection: "column", gap: "8px" }
              : {
                  border: "1px solid #e5e7eb",
                  borderRadius: "var(--radius-md)",
                  overflow: "hidden",
                }
          }
        >
          {/* Column headers — hidden on mobile (AC-1) */}
          {!isMobile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "8px 16px",
                backgroundColor: "#f9fafb",
                borderBottom: "1px solid #e5e7eb",
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#6b7280",
              }}
            >
              <div style={{ flex: "1 1 200px" }}>Task</div>
              <div style={{ flex: "0 0 120px" }}>Form</div>
              <div style={{ flex: "0 0 auto" }}>Due</div>
              <div style={{ flex: "0 0 auto" }}>Actions</div>
            </div>
          )}

          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              showClaimButton={activeTab === "unclaimed"}
              showCompleteButton={activeTab === "mine"}
              onComplete={(t) => {
                setClaimedTask(t);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Tab button — controlled by TaskInbox parent. */
function TabButton({
  active,
  onClick,
  label,
  count,
  isMobile = false,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  isMobile?: boolean;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: isMobile ? "12px 16px" : "8px 16px",
        minHeight: isMobile ? "48px" : "auto",
        border: "none",
        borderBottom: active ? "2px solid #3b82f6" : "2px solid transparent",
        backgroundColor: "transparent",
        color: active ? "#3b82f6" : "#6b7280",
        fontSize: "14px",
        fontWeight: active ? 600 : 400,
        cursor: "pointer",
        marginBottom: "-2px",
        width: isMobile ? "100%" : "auto",
        flex: isMobile ? 1 : "auto",
      }}
    >
      {label}
      {count !== undefined && (
        <span
          style={{
            marginLeft: "6px",
            padding: "1px 6px",
            borderRadius: "var(--radius-md)",
            backgroundColor: active ? "#dbeafe" : "#f3f4f6",
            color: active ? "#1d4ed8" : "#6b7280",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}
